'use client';
import React, { useState } from 'react';
import { Clock, CheckCircle, Scale, Award, RefreshCw, UserCheck, PenTool, Calendar, Hash, Info } from 'lucide-react';

const kpis = [
  { label: 'En Attente', value: '8', color: '#E67E22', bg: '#FEF5E7', critical: true },
  { label: 'Urgents', value: '3', color: '#C0392B', bg: '#FDEDEC', critical: true },
  { label: 'Signés Aujourd\'hui', value: '5', color: '#1E8449', bg: '#EAFAF1' },
  { label: 'Rejetés ce Mois', value: '12', color: '#C0392B', bg: '#FDEDEC' },
];

const dossiers = [
  { id: 'DG-2026-089', type: 'Arbitrage', societe: 'Kibali Gold Mining SARL', priorite: 'Urgent', delai: '2j', avis: 'Favorable', juriste: 'Lukusa A.', description: 'Conflit de périmètre avec État — arbitrage requis avant audience du 06/06', statut: 'En attente' },
  { id: 'DG-2026-090', type: 'Signature', societe: 'Congo Copper Corporation', priorite: 'Normal', delai: '5j', avis: 'En attente', juriste: 'Mutombo J.', description: 'Renouvellement titre PR-0045-2020 — dossier complet, avis favorable comptable', statut: 'En attente' },
  { id: 'DG-2026-091', type: 'Autorisation sensible', societe: 'Katanga Minerals Ltd', priorite: 'Urgent', delai: '1j', avis: 'Défavorable', juriste: 'Lukusa A.', description: 'Demande d\'extension périmètre zone protégée — avis environnemental défavorable', statut: 'En attente' },
  { id: 'DG-2026-092', type: 'Renouvellement stratégique', societe: 'Maniema Resources SA', priorite: 'Normal', delai: '7j', avis: 'Favorable', juriste: 'Kabila R.', description: 'Renouvellement PE-0078 — société en règle, paiements à jour', statut: 'En attente' },
  { id: 'DG-2026-093', type: 'Signature', societe: 'Ituri Mining Group', priorite: 'Urgent', delai: '3j', avis: 'En attente', juriste: 'Mutombo J.', description: 'Nouveau titre PE-G zone Ituri — étude d\'impact validée, attente signature DG', statut: 'En attente' },
  { id: 'DG-2026-094', type: 'Arbitrage', societe: 'Nord-Kivu Minerals', priorite: 'Normal', delai: '10j', avis: 'Favorable', juriste: 'Lukusa A.', description: 'Opposition tierce partie sur titre PR-0089 — arbitrage administratif', statut: 'En attente' },
  { id: 'DG-2026-095', type: 'Renouvellement stratégique', societe: 'Équateur Mining Corp', priorite: 'Normal', delai: '14j', avis: 'Favorable', juriste: 'Kabila R.', description: 'Renouvellement PE-0156 — première demande, société conforme', statut: 'En attente' },
  { id: 'DG-2026-096', type: 'Autorisation sensible', societe: 'Kasaï Resources Ltd', priorite: 'Urgent', delai: '2j', avis: 'En attente', juriste: 'Mutombo J.', description: 'Autorisation extraction substance rare — vérification ITRI requise', statut: 'En attente' },
];

interface HistoriqueItem {
  id: string;
  type: string;
  societe: string;
  decision: string;
  date: string;
  motif: string;
  horodatage: string;
  signataire: string;
  refSignature: string;
}

const historique: HistoriqueItem[] = [
  { id: 'DG-2026-085', type: 'Signature', societe: 'Sankuru Gold SARL', decision: 'Validé', date: '03/06/2026', motif: 'Dossier complet, conformité vérifiée', horodatage: '03/06/2026 14:32:07', signataire: 'Popol Mabolia Yenga', refSignature: 'SIG-2026-085-A' },
  { id: 'DG-2026-086', type: 'Arbitrage', societe: 'Lomami Mining', decision: 'Rejeté', date: '02/06/2026', motif: 'Documents insuffisants, renvoi pour complément', horodatage: '02/06/2026 10:15:44', signataire: 'Popol Mabolia Yenga', refSignature: 'SIG-2026-086-R' },
  { id: 'DG-2026-087', type: 'Renouvellement', societe: 'Haut-Katanga Resources', decision: 'Validé', date: '01/06/2026', motif: 'Renouvellement accordé pour 5 ans', horodatage: '01/06/2026 09:05:22', signataire: 'Popol Mabolia Yenga', refSignature: 'SIG-2026-087-A' },
  { id: 'DG-2026-088', type: 'Autorisation', societe: 'Kwilu Minerals', decision: 'Rejeté', date: '31/05/2026', motif: 'Zone protégée, autorisation refusée', horodatage: '31/05/2026 16:48:11', signataire: 'Popol Mabolia Yenga', refSignature: 'SIG-2026-088-R' },
];

const typeConfig: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  'Arbitrage': { icon: Scale, color: '#8E44AD', bg: '#F5EEF8' },
  'Signature': { icon: Award, color: '#1B4F72', bg: '#EBF5FB' },
  'Autorisation sensible': { icon: UserCheck, color: '#C0392B', bg: '#FDEDEC' },
  'Renouvellement stratégique': { icon: RefreshCw, color: '#1E8449', bg: '#EAFAF1' },
};

interface SignatureModalProps {
  dossier: typeof dossiers[0];
  action: 'valider' | 'rejeter';
  onConfirm: (motif: string) => void;
  onCancel: () => void;
}

function SignatureModal({ dossier, action, onConfirm, onCancel }: SignatureModalProps) {
  const [motif, setMotif] = useState('');
  const now = new Date();
  const horodatage = `${now.toLocaleDateString('fr-FR')} ${now.toLocaleTimeString('fr-FR')}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: action === 'valider' ? '#EAFAF1' : '#FDEDEC' }}
          >
            <PenTool size={20} style={{ color: action === 'valider' ? '#1E8449' : '#C0392B' }} />
          </div>
          <div>
            <h3 className="font-bold text-gray-800">
              {action === 'valider' ? 'Signature et Validation' : 'Signature et Rejet'}
            </h3>
            <p className="text-xs text-gray-400">Décision DG avec horodatage</p>
          </div>
        </div>

        {/* Dossier info */}
        <div className="rounded-xl p-3 mb-4" style={{ background: '#F4F6F8' }}>
          <div className="flex items-center gap-2 mb-1">
            <Hash size={12} className="text-gray-400" />
            <span className="text-xs font-mono font-semibold" style={{ color: '#1B4F72' }}>{dossier.id}</span>
          </div>
          <p className="text-sm font-semibold text-gray-800">{dossier.societe}</p>
          <p className="text-xs text-gray-500">{dossier.type}</p>
        </div>

        {/* Horodatage */}
        <div className="flex items-center gap-2 mb-4 p-3 rounded-xl" style={{ background: '#EBF5FB' }}>
          <Calendar size={14} style={{ color: '#1B4F72' }} />
          <div>
            <p className="text-xs font-semibold" style={{ color: '#1B4F72' }}>Horodatage automatique</p>
            <p className="text-xs text-gray-600 font-mono">{horodatage}</p>
          </div>
        </div>

        {/* Signataire */}
        <div className="flex items-center gap-2 mb-4 p-3 rounded-xl" style={{ background: '#F4F6F8' }}>
          <UserCheck size={14} className="text-gray-500" />
          <div>
            <p className="text-xs font-semibold text-gray-700">Signataire</p>
            <p className="text-xs text-gray-600">Popol Mabolia Yenga — Directeur Général</p>
          </div>
        </div>

        {/* Motif */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Motif de la décision <span className="text-red-500">*</span>
          </label>
          <textarea
            value={motif}
            onChange={e => setMotif(e.target.value)}
            placeholder={action === 'valider' ? 'Ex: Dossier conforme, conditions remplies...' : 'Ex: Documents manquants, non-conformité...'}
            rows={3}
            className="w-full border rounded-xl px-3 py-2 text-sm text-gray-700 outline-none resize-none"
            style={{ borderColor: '#e5e7eb' }}
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors hover:bg-gray-50"
            style={{ borderColor: '#e5e7eb', color: '#7F8C8D' }}
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm(motif || (action === 'valider' ? 'Décision favorable du DG' : 'Décision défavorable du DG'))}
           
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: action === 'valider' ? '#1E8449' : '#C0392B' }}
          >
            <span className="flex items-center justify-center gap-2">
              <PenTool size={14} />
              {action === 'valider' ? 'Signer et Valider' : 'Signer et Rejeter'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ValidationsDG() {
  const [dossiersList, setDossiersList] = useState(dossiers);
  const [hist, setHist] = useState<HistoriqueItem[]>(historique);
  const [signatureModal, setSignatureModal] = useState<{ dossier: typeof dossiers[0]; action: 'valider' | 'rejeter' } | null>(null);

  const openSignature = (dossier: typeof dossiers[0], action: 'valider' | 'rejeter') => {
    setSignatureModal({ dossier, action });
  };

  const handleConfirm = (motif: string) => {
    if (!signatureModal) return;
    const { dossier, action } = signatureModal;
    const now = new Date();
    const horodatage = `${now.toLocaleDateString('fr-FR')} ${now.toLocaleTimeString('fr-FR')}`;
    const refSig = `SIG-${dossier.id.replace('DG-', '')}-${action === 'valider' ? 'A' : 'R'}`;

    setHist(prev => [{
      id: dossier.id,
      type: dossier.type,
      societe: dossier.societe,
      decision: action === 'valider' ? 'Validé' : 'Rejeté',
      date: now.toLocaleDateString('fr-FR'),
      motif,
      horodatage,
      signataire: 'Popol Mabolia Yenga',
      refSignature: refSig,
    }, ...prev]);
    setDossiersList(prev => prev.filter(d => d.id !== dossier.id));
    setSignatureModal(null);
  };

  return (
    <div className="p-4 lg:p-6 space-y-5">
      {/* Signature Modal */}
      {signatureModal && (
        <SignatureModal
          dossier={signatureModal.dossier}
          action={signatureModal.action}
          onConfirm={handleConfirm}
          onCancel={() => setSignatureModal(null)}
        />
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k, i) => (
          <div
            key={i}
            className="bg-white rounded-xl border p-4"
            style={{ borderColor: k.critical ? '#C0392B' : '#e5e7eb', borderWidth: k.critical ? 2 : 1 }}
          >
            <p className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Odoo integration note */}
      <div className="bg-white rounded-xl border p-3 flex items-center gap-3" style={{ borderColor: '#D4AC0D', borderWidth: 1.5 }}>
        <Info size={16} style={{ color: '#D4AC0D' }} className="shrink-0" />
        <p className="text-xs text-gray-600">
          <span className="font-semibold" style={{ color: '#D4AC0D' }}>Intégration Odoo prévue :</span>{' '}
          Les signatures et validations tout cours seront synchronisées avec le module de workflow Odoo pour une traçabilité complète inter-systèmes.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Dossiers en attente */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Dossiers en Attente de Décision</h3>
            <span
              className="text-xs font-bold px-2 py-1 rounded-full text-white"
              style={{ background: '#C0392B' }}
            >
              {dossiersList.length} dossiers
            </span>
          </div>

          {dossiersList.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center" style={{ borderColor: '#e5e7eb' }}>
              <CheckCircle size={40} className="mx-auto mb-3 text-green-400" />
              <p className="font-semibold text-gray-700">Tous les dossiers ont été traités</p>
              <p className="text-sm text-gray-400 mt-1">Aucune décision en attente</p>
            </div>
          ) : (
            dossiersList.map((d) => {
              const tc = typeConfig[d.type] || { icon: Clock, color: '#7F8C8D', bg: '#F4F6F8' };
              const TypeIcon = tc.icon;
              return (
                <div
                  key={d.id}
                  className="bg-white rounded-xl border p-4"
                  style={{ borderColor: d.priorite === 'Urgent' ? '#C0392B' : '#e5e7eb', borderWidth: d.priorite === 'Urgent' ? 2 : 1 }}
                >
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: tc.bg }}>
                      <TypeIcon size={18} style={{ color: tc.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-gray-400">{d.id}</span>
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{
                            background: d.priorite === 'Urgent' ? '#FDEDEC' : '#EBF5FB',
                            color: d.priorite === 'Urgent' ? '#C0392B' : '#1B4F72',
                          }}
                        >
                          {d.priorite}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: tc.bg, color: tc.color }}>
                          {d.type}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-800">{d.societe}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{d.description}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock size={10} />
                          Délai: <strong className="text-gray-600">{d.delai}</strong>
                        </span>
                        <span>Juriste: <strong className="text-gray-600">{d.juriste}</strong></span>
                        <span>Avis: <strong style={{ color: d.avis === 'Favorable' ? '#1E8449' : d.avis === 'Défavorable' ? '#C0392B' : '#E67E22' }}>{d.avis}</strong></span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        onClick={() => openSignature(d, 'valider')}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-80 flex items-center gap-1"
                        style={{ background: '#1E8449' }}
                      >
                        <PenTool size={11} />
                        Signer
                      </button>
                      <button
                        onClick={() => openSignature(d, 'rejeter')}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-80"
                        style={{ background: '#C0392B' }}
                      >
                        Rejeter
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Historique avec horodatage */}
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
          <h3 className="font-semibold text-gray-800 mb-4">Historique des Décisions</h3>
          <div className="space-y-3">
            {hist.map((h, i) => (
              <div key={i} className="p-3 rounded-lg border" style={{ borderColor: '#f0f0f0', background: '#FAFAFA' }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-gray-400">{h.id}</span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{
                      background: h.decision === 'Validé' ? '#EAFAF1' : '#FDEDEC',
                      color: h.decision === 'Validé' ? '#1E8449' : '#C0392B',
                    }}
                  >
                    {h.decision}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-800">{h.societe}</p>
                <p className="text-xs text-gray-400">{h.type}</p>
                <p className="text-xs text-gray-500 mt-1 italic">{h.motif}</p>
                {/* Horodatage et Signature */}
                <div className="mt-2 pt-2 border-t space-y-1" style={{ borderColor: '#f0f0f0' }}>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Calendar size={10} />
                    <span className="font-mono">{h.horodatage}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <PenTool size={10} />
                    <span>{h.signataire}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs" style={{ color: '#1B4F72' }}>
                    <Hash size={10} />
                    <span className="font-mono font-semibold">{h.refSignature}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
